import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, TextField as MuiTextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/GetApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectArrayInput,
  SelectInput,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
  useNotify,
  usePermissions,
  useRefresh,
  useUnselectAll,
  AutocompleteInput,
} from 'react-admin';
import { Permission, ReportList } from "../../components";
import { CNPJField } from "../../components/Fields";
import { filterToQuery } from "../../utils";
import { MonthYearInput } from '../../components/Inputs';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from "../../constants";
import { InvoiceStatus } from '../../models/invoiceStatus';
import { PartnerPaymentStatus } from '../../models/partnerPaymentStatus';
import { ClientBillType } from '../../models/clientBillType';
import { GestaoClickStore } from '../../models/gestaoClickStore';
import GestaoClickCompareDialog from './GestaoClickCompareDialog';
import { invoiceTotals, invoiceTotalsFileName, invoiceTotalsWorkbook } from './invoiceTotals';

/**
 * Meses futuros liberados no seletor de competência.
 *
 * A competência é o mês do vencimento, e o boleto é emitido com antecedência — em agosto já existem
 * cobranças com competência em setembro e outubro. Travar o seletor no mês atual esconde essas
 * cobranças do relatório.
 */
const MESES_DE_ANTECEDENCIA = 2;

/** Cancelada é boleto que sumiu do Gestão Click: fica fora do relatório até alguém marcar. */
const STATUS_PADRAO = InvoiceStatus.InvoiceStatusChoices
  .map(choice => choice.id)
  .filter(status => status !== InvoiceStatus.canceled);

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold'
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const BulkPayAction = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [observation, setObservation] = useState('');
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const { selectedIds } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('reports/invoices');

  const selectedData = useMemo(() => {
    if (!data || !selectedIds.length) return { ids: [], count: 0, totalValue: 0, retailerValue: 0, sameRetailer: true, allPaid: true, retailerId: null };

    const selected = data.filter(item => selectedIds.includes(item.id));
    const retailerIds = [...new Set(selected.map(item => item.retailerId))];
    const sameRetailer = retailerIds.length === 1;
    const allPaid = selected.every(item => item.status === 'paid');

    return {
      ids: selected.map(item => item.id),
      count: selected.length,
      totalValue: selected.reduce((sum, item) => sum + (item.value || 0), 0),
      retailerValue: selected.reduce((sum, item) => sum + (item.retailerValue || 0), 0),
      sameRetailer,
      allPaid,
      retailerId: sameRetailer ? retailerIds[0] : null
    };
  }, [data, selectedIds]);

  const payDisabled = !selectedData.sameRetailer || !selectedData.allPaid;
  const payTooltip = !selectedData.sameRetailer
    ? 'Selecione cobranças da mesma revenda para pagar'
    : !selectedData.allPaid
      ? 'Selecione apenas cobranças pagas pelo cliente'
      : '';

  const handlePay = async () => {
    if (!file) {
      notify('Selecione um arquivo', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await dataProvider.create('partner-payments', {
        data: {
          file: file,
          clientBillIds: JSON.stringify(selectedData.ids),
          retailerId: selectedData.retailerId,
          observation: observation || undefined
        }
      });
      notify('Pagamento registrado com sucesso!', { type: 'success' });
      unselectAll();
      refresh();
      setOpen(false);
      setFile(null);
      setObservation('');
    } catch (error) {
      notify('Erro ao registrar pagamento', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCancel = async () => {
    if (!window.confirm(`Cancelar ${selectedData.count} cobrança(s)?`)) return;

    setCancelLoading(true);
    try {
      await dataProvider.create('client-bills/bulk-cancel', {
        data: { ids: selectedData.ids }
      });
      notify(`${selectedData.count} cobrança(s) cancelada(s)`, { type: 'success' });
      unselectAll();
      refresh();
    } catch (error) {
      notify('Erro ao cancelar cobranças', { type: 'error' });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setFile(null);
      setObservation('');
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Tooltip title={payTooltip} disableHoverListener={!payDisabled}>
        <span>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)} disabled={payDisabled}>
            Pagar Parceiro
          </Button>
        </span>
      </Tooltip>
      <Button
        variant="outlined"
        color="error"
        onClick={handleBulkCancel}
        disabled={cancelLoading}
      >
        {cancelLoading ? 'Cancelando...' : 'Cancelar Selecionadas'}
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Registrar Pagamento de Parceiro
        </DialogTitle>
        <DialogContent>
          <Box sx={{
            bgcolor: 'grey.100',
            borderRadius: 2,
            p: 2,
            mb: 3
          }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Resumo do Pagamento
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
              <Typography variant="body2" color="textSecondary">Cobranças selecionadas:</Typography>
              <Typography variant="body2" fontWeight="medium">{selectedData.count}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="textSecondary">Valor total dos boletos:</Typography>
              <Typography variant="body2">{formatCurrency(selectedData.totalValue)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="textSecondary">Valor do Parceiro:</Typography>
              <Typography variant="body1" fontWeight="bold" color="primary">
                {formatCurrency(selectedData.retailerValue)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Comprovante de Pagamento
            </Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: file ? 'success.main' : 'grey.300',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                bgcolor: file ? 'success.50' : 'grey.50',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={e => setFile(e.target.files[0])}
                style={{ display: 'none' }}
                id="payment-file-input"
              />
              <label htmlFor="payment-file-input">
                <Button variant="outlined" component="span" size="small">
                  {file ? 'Trocar arquivo' : 'Selecionar arquivo'}
                </Button>
              </label>
              {file && (
                <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                  {file.name}
                </Typography>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                PDF, PNG ou JPG (máx. 10MB)
              </Typography>
            </Box>
          </Box>

          <MuiTextField
            label="Observação"
            value={observation}
            onChange={e => setObservation(e.target.value)}
            multiline
            rows={2}
            fullWidth
            variant="outlined"
            placeholder="Observação opcional sobre o pagamento"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handlePay}
            variant="contained"
            color="primary"
            disabled={!file || loading}
          >
            {loading ? 'Processando...' : 'Confirmar Pagamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const EditClientBillButton = ({ record }) => {
  const navigate = useNavigate();
  if (!record) return null;
  return (
    <Tooltip title="Editar cobrança">
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/client-bills/${record.id}`); }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const ListInvoices = () => {
  const props = useListContext();
  const { permissions } = usePermissions();
  const isAdmin = Boolean(permissions?.includes(PERMISSIONS.ADMIN));

  const reportInfo = props.data?.[0];
  const data = reportInfo?.data ?? [];
  const summaryData = useMemo(
    () => reportInfo ? invoiceTotals(reportInfo.summaryData, isAdmin) : [],
    [reportInfo, isAdmin]
  );

  // Limpar seleção e cache quando FILTROS mudam (não quando muda de página)
  const itemCacheRef = useRef(new Map());
  const prevFiltersRef = useRef(props.filterValues);
  useEffect(() => {
    if (JSON.stringify(prevFiltersRef.current) !== JSON.stringify(props.filterValues)) {
      itemCacheRef.current = new Map();
      if (props.selectedIds?.length > 0) {
        props.onSelect([]);
      }
    }
    prevFiltersRef.current = props.filterValues;
  }, [props.filterValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cachear itens vistos para que o BulkPayAction acesse itens de outras páginas
  data.forEach(item => itemCacheRef.current.set(item.id, item));
  const cachedData = [...itemCacheRef.current.values()];

  return (
    <>
      <Typography sx={titleSx}>Totais</Typography>
      <Datagrid bulkActionButtons={false} style={{ marginBottom: 60 }} data={summaryData} total={1}>
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <ReferenceField
            label="Revenda"
            source="retailer_id"
            reference="retailers"
            textAlign="center"
            emptyText="Todos"
            link={false}
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
        }
        <NumberField source="count" textAlign="center" label="Quantidade" sortable={false} />
        <NumberField
          source="total"
          label="Valor Total"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="totalretailervalue"
          label="Valor dos parceiros"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
      </Datagrid>

      <Typography sx={titleSx}>Cobranças</Typography>
      <Datagrid
        resource={props.resource}
        data={data}
        bulkActionButtons={permissions && permissions.includes(PERMISSIONS.ADMIN) ? <BulkPayAction data={cachedData} /> : false}
      >
        <ReferenceField label="Rede" reference='chains' source="chainId" sortBy="chain.name" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Posto"
          source="placeId"
          reference="stations"
          sortBy="place.name"
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="CNPJ"
          source="placeId"
          reference="stations"
          sortBy="place.cnpj"
          sx={{ whiteSpace: 'nowrap' }}
        >
          <CNPJField source="cnpj" />
        </ReferenceField>
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <SelectField
            source="type"
            label="Tipo"
            textAlign="center"
            emptyText="—"
            choices={ClientBillType.ClientBillTypeChoices}
          />
        }
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <SelectField
            source="gestaoClickStoreId"
            label="Loja"
            textAlign="center"
            emptyText="—"
            choices={GestaoClickStore.GestaoClickStoreChoices}
          />
        }
        <NumberField
          source="value"
          label="Valor do boleto"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <NumberField
          source="retailerValue"
          label="Valor do Parceiro"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <DateField source="dueDate" label="Vencimento" textAlign="center" emptyText="--" locales="pt-BR" />
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <ReferenceField
            label="Revenda"
            source="retailerId"
            reference="retailers"
            textAlign="center"
            emptyText="Sem Revenda"
            link={false}
            sortBy="retailer.name"
          >
            <TextField source="name" />
          </ReferenceField>
        }
        <SelectField
          source="status"
          label="Status Cliente"
          textAlign="center"
          choices={InvoiceStatus.InvoiceStatusChoices}
        />
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <SelectField
            source="partnerStatus"
            label="Status Parceiro"
            textAlign="center"
            choices={PartnerPaymentStatus.PartnerPaymentStatusChoices}
          />
        }
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <FunctionField
            label="Gestão Click"
            textAlign="center"
            sortable={false}
            render={record => record?.gestaoClickId ? (
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={(e) => { e.stopPropagation(); window.open(`https://gestaoclick.com/financeiro/movimentacoes_financeiras/visualizar_recebimento/${record.gestaoClickId}?retorno=%2Fmovimentacoes_financeiras%2Findex_recebimento`, '_blank'); }}
              >
                Gestão Click
              </Button>
            ) : null}
          />
        }
        {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
          <FunctionField
            label=""
            render={record => <EditClientBillButton record={record} />}
          />
        }
      </Datagrid>
    </>
  );
};

const ListFilters = props => {
  const { permissions } = usePermissions();
  // filterValues vem do contexto da lista, não das props — o Filter é montado pelo ListToolbar.
  const { filterValues } = useListContext();

  return <Filter {...props}>
    <MonthYearInput
      source="startMonth"
      label="Competência Inicial"
      format={date => moment(date).startOf('month').toISOString()}
      maxDate={moment().add(MESES_DE_ANTECEDENCIA, 'month')}
      alwaysOn
    />
    <MonthYearInput
      source="endMonth"
      label="Competência Final"
      format={date => moment(date).startOf('month').toISOString()}
      maxDate={moment().add(MESES_DE_ANTECEDENCIA, 'month')}
      alwaysOn
    />

    {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
      <ReferenceInput source="retailerId" reference="retailers" sort={{ field: 'name', order: 'ASC' }} perPage={null} alwaysOn>
        <SelectInput label="Revenda" optionText="name" emptyText="Todos" />
      </ReferenceInput>
    }
    {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
      <ReferenceInput
        source="chainId"
        reference="chains"
        sort={{ field: 'name', order: 'ASC' }}
        perPage={null}
        alwaysOn
      >
        <AutocompleteInput
          filterToQuery={filterToQuery}
          label="Rede"
          optionText="name"
        />
      </ReferenceInput>}
    {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
      <ReferenceInput
        source="placeId"
        reference="stations"
        sort={{ field: 'name', order: 'ASC' }}
        perPage={null}
        // Com rede selecionada, a lista de postos já vem restrita a ela.
        filter={filterValues?.chainId ? { chainId: filterValues.chainId } : undefined}
        alwaysOn
      >
        <AutocompleteInput
          filterToQuery={filterToQuery}
          label="Posto"
          optionText="name"
        />
      </ReferenceInput>}
    <SelectInput
      source="type"
      label="Tipo"
      choices={ClientBillType.ClientBillTypeChoices}
      emptyText="Todos"
      alwaysOn
    />
    <SelectInput
      source="gestaoClickStoreId"
      label="Loja"
      choices={GestaoClickStore.GestaoClickStoreChoices}
      emptyText="Todas"
      alwaysOn
    />
    <SelectArrayInput
      source="status"
      label="Status Cliente"
      choices={InvoiceStatus.InvoiceStatusChoices}
      // Link antigo traz um status só, em texto; o select múltiplo só aceita lista.
      format={value => value == null ? [] : [].concat(value)}
      alwaysOn
    />
    {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
      <SelectInput
        source="partnerStatus"
        label="Status Parceiro"
        choices={PartnerPaymentStatus.PartnerPaymentStatusChoices}
        emptyText="Todos"
        alwaysOn
      />
    }
  </Filter>
}

const ExportTotalsButton = () => {
  const { data, filterValues, isFetching } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [exporting, setExporting] = useState(false);
  const summaryData = data?.[0]?.summaryData;

  const handleExport = async () => {
    setExporting(true);
    try {
      const totals = invoiceTotals(summaryData, true);
      const ids = totals.map(row => row.retailer_id).filter(Boolean);
      const { data: retailers } = await dataProvider.getMany('retailers', { ids });
      XLSX.writeFile(invoiceTotalsWorkbook(totals, retailers), invoiceTotalsFileName(filterValues));
    } catch (error) {
      notify('Erro ao exportar dados!', { type: 'warning' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={exporting || isFetching || !summaryData?.length}
      startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
      size="small"
    >
      Exportar totais
    </Button>
  );
};

const InvoicesActions = ({ reportRef }) => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const { filterValues } = useListContext();
  const [loading, setLoading] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const handleInvokeBilling = async () => {
    setLoading(true);
    try {
      await dataProvider.create('partner-payments/invoke-billing', { data: {} });
      notify('Cobranças atualizadas com sucesso!', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Erro ao atualizar cobranças', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TopToolbar sx={{ alignItems: 'center' }}>
      <Button
        onClick={() => navigate('/skipped-billings')}
        startIcon={<FactCheckIcon />}
        size="small"
      >
        Reconciliação
      </Button>
      {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
        <Button
          onClick={handleInvokeBilling}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : <SyncIcon />}
          size="small"
        >
          {loading ? 'Atualizando...' : 'Atualizar Cobranças'}
        </Button>
      }
      {permissions && permissions.includes(PERMISSIONS.ADMIN) && <ExportTotalsButton />}
      {permissions && permissions.includes(PERMISSIONS.ADMIN) &&
        <Button
          onClick={() => setCompareOpen(true)}
          disabled={!filterValues?.startMonth}
          startIcon={<CompareArrowsIcon />}
          size="small"
        >
          Conferir com Gestão Click
        </Button>
      }
      {compareOpen &&
        <GestaoClickCompareDialog
          open
          onClose={() => setCompareOpen(false)}
          startMonth={filterValues.startMonth}
          endMonth={filterValues.endMonth}
        />
      }
    </TopToolbar>
  );
};

const ReportInvoices = props => {
  const defaultMonth = moment().startOf('month').toISOString();

  return (
    <Permission permission={PERMISSIONS.INVOICES_REPORT}>
      <ReportList
        {...props}
        filterDefaultValues={{ startMonth: defaultMonth, endMonth: defaultMonth, status: STATUS_PADRAO }}
        basePath="report-invoices"
        resource="reports/invoices"
        title="Relatório de Cobranças"
        filters={<ListFilters />}
        actions={<InvoicesActions />}
        sort={{ field: 'chain.name', order: 'ASC' }}
        perPage={25}
        pagination={<ListPagination />}
        titleOnPrint="Relatório de Cobranças"
      >
        <ListInvoices />
      </ReportList>
    </Permission>
  )
};

export default ReportInvoices;